import {
  ChevronsUpDown,
  CirclePlay,
  LogOut,
  Mail,
  MessageCircle,
  Monitor,
  Moon,
  Rocket,
  Settings,
  Sun,
  Users,
  type LucideIcon
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarImage
} from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Kbd } from "@/components/ui/kbd";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar
} from "@/components/ui/sidebar";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  DensityRow,
  useDensityNotice,
  useDisplaySettingsMenu
} from "@/features/density";
import { restartOnboarding } from "@/features/onboarding/hooks/useOnboarding";
import { ONBOARDING_PATH } from "@/features/onboarding/routes";
import { useTheme, type ThemePreference } from "@/features/theme";
import { tutorialsFor, useTutorialsLater } from "@/features/tutorials";
import { TUTORIALS_PATH } from "@/features/tutorials/routes";
import { auth } from "../../api/firebaseApi";
import { UsersAPI } from "../../api/usersApi";
import { isSupportChatAvailable, openSupport } from "../../helpers/helpers";
import { normalizeAvatarSrc } from "../../helpers/userDisplay";
import { useGetUserDetails } from "../../hooks/useGetUserDetails";
import { useLogOut } from "../../hooks/useLogOut";
import { useSupportUnread } from "../../hooks/useSupportUnread";
import { updateForeignNationalAvatar } from "../../redux/foreignNational/foreignNationalSlice";
import { updateIndividualAvatar } from "../../redux/individual/individualSlice";
import { updateLawyerAvatar } from "../../redux/lawyer/lawyerSlice";
import { SettingsModal } from "../settings/SettingsModal";

const MENU_ITEM = "gap-2 px-2";
// Explicit size class so DropdownMenuItem's `svg:not([class*='size-'])` rule
// doesn't win and force the icon back to size-4.
const MENU_ICON = "size-3.5 text-fg-3";

const THEME_OPTIONS: {
  value: ThemePreference;
  label: string;
  Icon: LucideIcon;
}[] = [
  { value: "light", label: "Light", Icon: Sun },
  { value: "dark", label: "Dark", Icon: Moon },
  { value: "system", label: "System", Icon: Monitor }
];

const isSettingsShortcut = (e: KeyboardEvent): boolean =>
  (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key === ",";

/** Theme row: the current choice, muted, before the chevron; Light / Dark / System inside. */
function ThemeRow() {
  const { preference, resolvedTheme, setPreference } = useTheme();
  const TriggerIcon = resolvedTheme === "dark" ? Moon : Sun;
  const current =
    THEME_OPTIONS.find((o) => o.value === preference)?.label ?? "System";
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className={MENU_ITEM}>
        <TriggerIcon className={MENU_ICON} />
        <span className="flex-1">Theme</span>
        <span className="text-sm text-fg-3">{current}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-40">
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => {
            const next = THEME_OPTIONS.find((o) => o.value === value);
            if (next) setPreference(next.value);
          }}
        >
          {THEME_OPTIONS.map(({ value, label, Icon }) => (
            <DropdownMenuRadioItem
              key={value}
              value={value}
              className={`${MENU_ITEM} pr-8`}
            >
              <Icon className={MENU_ICON} />
              {label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}

const initialsOf = (fullName: string): string => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] ?? ""}${
    parts[parts.length - 1][0] ?? ""
  }`.toUpperCase();
};

function MenuAvatar({
  src,
  initials,
  unread = false,
  newTutorial = false
}: {
  src: string;
  initials: string;
  /** A support reply is waiting: an accent dot on the avatar. */
  unread?: boolean;
  /** A tutorial was put off with Later: the same dot, until it's opened. */
  newTutorial?: boolean;
}) {
  return (
    <Avatar>
      <AvatarImage src={src || undefined} alt="" />
      <AvatarFallback>{initials}</AvatarFallback>
      {unread || newTutorial ? (
        <AvatarBadge
          className="ring-sidebar"
          data-testid={unread ? "support-unread-dot" : "tutorial-new-dot"}
        />
      ) : null}
    </Avatar>
  );
}

/**
 * Best-effort avatar preload: if the store has no avatar yet, fetch the
 * profile a few times (the auth token can arrive late) and push it into the
 * matching user slice.
 */
function useAvatarPreload(
  uid: string,
  userType: string,
  normalizedAvatarSrc: string
) {
  const dispatch = useDispatch();
  const attemptsRef = useRef<Record<string, number>>({});
  const retryTimerRef = useRef<number | null>(null);
  const [retrySeq, setRetrySeq] = useState(0);

  useEffect(() => {
    const id = uid.trim();
    if (!id || normalizedAvatarSrc) return () => {};
    const attempts = attemptsRef.current[id] ?? 0;
    if (attempts >= 3) return () => {};
    attemptsRef.current[id] = attempts + 1;

    let cancelled = false;
    (async () => {
      try {
        const profile = await UsersAPI.getProfile(id);
        const url = String(profile?.avatar_data_url ?? "").trim();
        if (!url || cancelled) return;
        const payload = { avatarSrc: url, avatarName: "" };
        if (userType === "Lawyer") dispatch(updateLawyerAvatar(payload));
        else if (userType === "Individual")
          dispatch(updateIndividualAvatar(payload));
        else if (userType === "ForeignNational")
          dispatch(updateForeignNationalAvatar(payload));
      } catch {
        if (cancelled || (attemptsRef.current[id] ?? 0) >= 3) return;
        if (retryTimerRef.current) window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = window.setTimeout(
          () => setRetrySeq((v) => v + 1),
          2000
        );
      }
    })();

    return () => {
      cancelled = true;
      if (retryTimerRef.current) {
        window.clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [uid, userType, normalizedAvatarSrc, dispatch, retrySeq]);
}

/** Sidebar footer: avatar + identity, with the account menu (sidebar-09 nav-user). */
export function NavUser() {
  const { isMobile } = useSidebar();
  const navigate = useNavigate();
  const { onSubmitLogOut } = useLogOut();
  const {
    userDetails: { userFullName, avatarSrc, email, id: uid, userType }
  } = useGetUserDetails();

  const normalizedAvatarSrc = useMemo(
    () => normalizeAvatarSrc(avatarSrc),
    [avatarSrc]
  );
  useAvatarPreload(uid ?? "", userType, normalizedAvatarSrc);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const tutorials = useMemo(() => tutorialsFor(userType), [userType]);
  const tutorialsLater = useTutorialsLater(uid ?? "");
  const tutorialPending = tutorials.length > 0 && tutorialsLater.length > 0;
  const supportUnread = useSupportUnread();
  // Read when the menu opens: the Messenger boots a few seconds after load.
  const [supportChat, setSupportChat] = useState(isSupportChatAvailable);
  const initials = useMemo(() => initialsOf(userFullName), [userFullName]);
  const displayEmail = email || auth.currentUser?.email || "";

  const profilePath =
    userType === "Individual" ? `/individual/${uid}/account/profile` : null;

  const openSettings = () => {
    if (userType === "Lawyer") {
      setSettingsOpen(true);
      return;
    }
    navigate(
      profilePath ?? (userType === "ForeignNational" ? "/portal" : "/cases")
    );
  };

  const refreshSupportChat = useCallback(
    () => setSupportChat(isSupportChatAvailable()),
    []
  );

  const isLawyer = userType === "Lawyer";
  useDensityNotice(isLawyer);
  // The density notice's "Display Settings" link opens this menu with the
  // Display submenu already expanded.
  const displayMenu = useDisplaySettingsMenu(refreshSupportChat);
  useEffect(() => {
    if (!isLawyer) return undefined;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isSettingsShortcut(e)) return;
      e.preventDefault();
      setSettingsOpen(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isLawyer]);

  const getStarted = () => {
    restartOnboarding()
      .then(() => navigate(ONBOARDING_PATH))
      .catch(() => toast.error("Could not open Get Started. Try again."));
  };

  const signOut = async () => {
    if (auth.currentUser) await onSubmitLogOut();
    else navigate("/login");
  };

  const showTeam =
    import.meta.env.REACT_APP_ENABLE_TEAM_MENU === "true" &&
    email.toLowerCase().endsWith("@caseblink.com");

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu
          open={displayMenu.menuOpen}
          onOpenChange={(open) => {
            displayMenu.onMenuOpenChange(open);
            if (open) refreshSupportChat();
          }}
        >
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="h-12 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8!"
              aria-label={
                supportUnread > 0
                  ? "Account menu, unread reply from support"
                  : tutorialPending
                  ? "Account menu, new tutorial"
                  : "Account menu"
              }
            >
              <MenuAvatar
                src={normalizedAvatarSrc}
                initials={initials}
                unread={supportUnread > 0}
                newTutorial={tutorialPending}
              />
              <div className="grid flex-1 text-left leading-tight">
                <span className="truncate text-md font-medium">
                  {userFullName}
                </span>
                <span className="truncate text-sm text-muted-foreground">
                  {displayEmail}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-68 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left">
                <MenuAvatar src={normalizedAvatarSrc} initials={initials} />
                <div className="grid flex-1 text-left leading-tight">
                  <span className="truncate text-md font-medium text-fg">
                    {userFullName}
                  </span>
                  <span className="truncate text-sm text-fg-3">
                    {displayEmail}
                  </span>
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem className={MENU_ITEM} onSelect={openSettings}>
                <Settings className={MENU_ICON} />
                Settings
                {isLawyer ? (
                  <DropdownMenuShortcut>
                    <Kbd>⌘,</Kbd>
                  </DropdownMenuShortcut>
                ) : null}
              </DropdownMenuItem>
              {isLawyer ? (
                <DropdownMenuItem
                  className={MENU_ITEM}
                  onSelect={getStarted}
                  data-testid="menu-get-started"
                >
                  <Rocket className={MENU_ICON} />
                  Get Started
                </DropdownMenuItem>
              ) : null}
              <ThemeRow />
              <DensityRow
                itemClassName={MENU_ITEM}
                iconClassName={MENU_ICON}
                open={displayMenu.displayOpen}
                onOpenChange={displayMenu.onDisplayOpenChange}
              />
              {showTeam ? (
                <DropdownMenuItem
                  className={MENU_ITEM}
                  onSelect={() => navigate("/admin/users")}
                >
                  <Users className={MENU_ICON} />
                  Team
                </DropdownMenuItem>
              ) : null}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {tutorials.length > 0 ? (
                <DropdownMenuItem
                  className={MENU_ITEM}
                  onSelect={() => navigate(TUTORIALS_PATH)}
                  data-testid="menu-tutorials"
                >
                  <CirclePlay className={MENU_ICON} />
                  Tutorials
                  {tutorialPending ? (
                    <StatusBadge
                      tone="accent"
                      className="ml-auto"
                      data-testid="menu-tutorials-new"
                    >
                      New
                    </StatusBadge>
                  ) : null}
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem
                className={MENU_ITEM}
                onSelect={() => openSupport()}
                data-testid="menu-contact-support"
              >
                {supportChat ? (
                  <MessageCircle className={MENU_ICON} />
                ) : (
                  <Mail className={MENU_ICON} />
                )}
                <span className="flex-1">
                  {supportChat ? "Contact Support" : "Email Support"}
                </span>
                {supportUnread > 0 ? (
                  <span
                    className="size-1.5 rounded-full bg-primary"
                    aria-label="Unread reply"
                  />
                ) : null}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem className={MENU_ITEM} onSelect={signOut}>
              <LogOut className={MENU_ICON} />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </SidebarMenu>
  );
}
